'use client';

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mantine/core';

import SegmentedControl from '@ui-kit/molecules/SegmentedControl';
import AutomaticView from './AutomaticView';
import SimpleView from './SimpleView';
import ResultsView from './ResultsView';

import { useSuggestRenameMutation } from '@app-types/blackroom-api';
import {
  ResourceNameChangeProps,
  ResourceNameChangeItem,
  ResourceNameChangeRef,
} from './types';

import { useRequestQueue } from './hooks/useRequestQueue';

const ResourceNameChange = forwardRef<
  ResourceNameChangeRef,
  ResourceNameChangeProps
>(function ResourceNameChange(
  {
    resources,
    onChangesUpdate,
    onResultsStepChange,
    onSelectedPromptChange,
    onActiveTabChange,
    customPrompt,
    maxConcurrent = 5,
    automaticOnly = false,
  },
  ref,
) {
  const { t } = useTranslation();

  const isMountedRef = useRef(true);
  const onChangesUpdateRef = useRef(onChangesUpdate);

  useEffect(() => {
    onChangesUpdateRef.current = onChangesUpdate;
  }, [onChangesUpdate]);

  const resourceIds = resources.map((r) => r.id).join(',');

  const [suggestedNames, setSuggestedNames] = useState<Record<string, string>>(
    () => {
      const initialNames: Record<string, string> = {};
      resources.forEach((resource) => {
        initialNames[resource.id] = resource.name;
      });
      return initialNames;
    },
  );
  const [loadingResources, setLoadingResources] = useState<Set<string>>(
    new Set(),
  );
  const [hasFetchedSuggestions, setHasFetchedSuggestions] = useState(false);

  const [activeTab, setActiveTab] = useState('automatic');
  const [selectedPromptKey, setSelectedPromptKey] = useState<string | null>(
    null,
  );
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [userCustomPrompt, setUserCustomPrompt] = useState(customPrompt || '');

  const [suggestRenameMutation] = useSuggestRenameMutation();

  const { addToQueue, clearQueue, getQueueStatus } = useRequestQueue<string>({
    maxConcurrent,
  });

  const handleSuggestRename = useCallback(
    async (
      resourceId: string,
      workspaceId: string,
      promptOverride?: string,
    ): Promise<string> => {
      if (isMountedRef.current) {
        setLoadingResources((prev) => new Set(prev).add(resourceId));
      }

      try {
        const result = await addToQueue(resourceId, async (abortSignal) => {
          const response = await suggestRenameMutation({
            variables: {
              suggestRenameInput: {
                resourceId,
                workspaceId,
                userPrompt: promptOverride || selectedPrompt || customPrompt,
              },
            },
            context: {
              fetchOptions: {
                signal: abortSignal,
              },
            },
          });
          return response.data?.suggestRename ?? '';
        });
        return result;
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message === 'Request cancelled' || error.name === 'AbortError')
        ) {
          return '';
        }
        console.error(
          `Error suggesting rename for resource ${resourceId}:`,
          error,
        );
        return '';
      } finally {
        if (isMountedRef.current) {
          setLoadingResources((prev) => {
            const newSet = new Set(prev);
            newSet.delete(resourceId);
            return newSet;
          });
        }
      }
    },
    [addToQueue, suggestRenameMutation, customPrompt, selectedPrompt],
  );

  const handleRegenerateSuggestion = async (resourceId: string) => {
    const resource = resources.find((r) => r.id === resourceId);
    if (!resource || !isMountedRef.current) return;

    const newName = await handleSuggestRename(resourceId, resource.workspaceId);
    if (newName && isMountedRef.current) {
      setSuggestedNames((prev) => ({
        ...prev,
        [resourceId]: newName,
      }));
    }
  };

  const handleNameChange = (resourceId: string, value: string) => {
    setSuggestedNames((prev) => ({
      ...prev,
      [resourceId]: value,
    }));
  };

  const handleGenerateAll = async (promptOverride?: string) => {
    if (!isMountedRef.current) return;

    setHasFetchedSuggestions(true);

    const promises = resources.map(async (resource) => {
      if (!isMountedRef.current) return;

      const newName = await handleSuggestRename(
        resource.id,
        resource.workspaceId,
        promptOverride,
      );

      if (isMountedRef.current && newName) {
        setSuggestedNames((prev) => ({
          ...prev,
          [resource.id]: newName,
        }));
      }
    });

    await Promise.allSettled(promises);
  };

  const handleSelectPrompt = (key: string | null, prompt: string) => {
    setSelectedPromptKey(key);
    setSelectedPrompt(prompt);
  };

  const handleCustomPromptChange = (value: string) => {
    setUserCustomPrompt(value);
    if (selectedPromptKey === 'customPrompt') {
      setSelectedPrompt(value);
    }
  };

  useEffect(() => {
    onSelectedPromptChange?.(selectedPrompt);
  }, [selectedPrompt, onSelectedPromptChange]);

  useEffect(() => {
    onActiveTabChange?.(activeTab);
  }, [activeTab, onActiveTabChange]);

  useEffect(() => {
    const changes = new Map<string, ResourceNameChangeItem>();

    resources.forEach((resource) => {
      const suggestedName = suggestedNames[resource.id];
      if (suggestedName !== undefined) {
        changes.set(resource.id, {
          resourceId: resource.id,
          newName: suggestedName,
        });
      }
    });

    onChangesUpdateRef.current(changes);
  }, [suggestedNames, resourceIds]);

  useEffect(() => {
    onResultsStepChange?.(hasFetchedSuggestions);
  }, [hasFetchedSuggestions, onResultsStepChange]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearQueue();
      setLoadingResources(new Set());
    };
  }, [clearQueue]);

  useImperativeHandle(
    ref,
    () => ({
      cancelAllRequests: () => {
        clearQueue();
        if (isMountedRef.current) {
          setLoadingResources(new Set());
        }
      },
      resetToInitialState: () => {
        if (isMountedRef.current) {
          setHasFetchedSuggestions(false);
          setSuggestedNames({});
          setLoadingResources(new Set());
          setSelectedPromptKey(null);
          setSelectedPrompt('');
          setUserCustomPrompt(customPrompt || '');
          setActiveTab('automatic');
        }
      },
      goBackToPreviousStep: () => {
        if (isMountedRef.current && hasFetchedSuggestions) {
          clearQueue();
          setLoadingResources(new Set());
          setHasFetchedSuggestions(false);

          const initialNames: Record<string, string> = {};
          resources.forEach((resource) => {
            initialNames[resource.id] = resource.name;
          });
          setSuggestedNames(initialNames);
        }
      },
      getQueueStatus,
      getSelectedPrompt: () => selectedPrompt,
      isAutomatic: () => activeTab === 'automatic',
      generateAll: handleGenerateAll,
    }),
    [
      clearQueue,
      getQueueStatus,
      customPrompt,
      hasFetchedSuggestions,
      resources,
      selectedPrompt,
      activeTab,
      handleGenerateAll,
    ],
  );

  if (hasFetchedSuggestions) {
    return (
      <ResultsView
        resources={resources}
        suggestedNames={suggestedNames}
        loadingResources={loadingResources}
        onNameChange={handleNameChange}
        onRegenerateSuggestion={handleRegenerateSuggestion}
      />
    );
  }

  return (
    <Stack gap="md" style={{ flex: 1, minHeight: 0, width: '100%' }}>
      {!automaticOnly && (
        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          data={[
            {
              value: 'automatic',
              label: t('Resource:DialogResourceEdit.tabs.automatic'),
            },
            {
              value: 'manual',
              label: t('Resource:DialogResourceEdit.tabs.manual'),
            },
          ]}
          fullWidth
        />
      )}

      {activeTab === 'automatic' || automaticOnly ? (
        <AutomaticView
          selectedPromptKey={selectedPromptKey}
          customPrompt={userCustomPrompt}
          onSelectPrompt={handleSelectPrompt}
          onCustomPromptChange={handleCustomPromptChange}
        />
      ) : (
        <SimpleView
          resources={resources}
          suggestedNames={suggestedNames}
          onNameChange={handleNameChange}
        />
      )}
    </Stack>
  );
});

export default ResourceNameChange;
export type { ResourceNameChangeRef };
